import { useTenantMetadata } from '@globals/useTenantMetadata'
import { LanguagesEnum } from '@kudos/http-client'

/**
 * Generic type for objects that have a translations field.
 * The translations field maps language codes to objects with translated field values.
 */
type TranslatableObject<T extends Record<string, unknown>> = {
  translations?:
    | {
        [key in LanguagesEnum]?: Partial<T>
      }
    | null
} & T

/**
 * Hook to get translated field values from objects with translations.
 * Falls back to the default field value if translation is not available.
 *
 * @returns A function that extracts the translated value for a given field
 *
 * @example
 * ```tsx
 * const getTranslatedField = useTranslatedField()
 * const translatedName = getTranslatedField(awardType, 'name')
 * const translatedDescription = getTranslatedField(awardType, 'description')
 * ```
 */
export const useTranslatedField = () => {
  const [{ language }] = useTenantMetadata()

  /**
   * Gets the translated value for a field, falling back to the default field value.
   *
   * @param obj - Object with translations field and default field values
   * @param fieldName - Name of the field to translate (e.g., 'name', 'title', 'description')
   * @returns The translated value if available, otherwise the default field value
   */
  const getTranslatedField = <T extends Record<string, unknown>>(
    obj: TranslatableObject<T> | null | undefined,
    fieldName: keyof T,
  ): string => {
    if (!obj) {
      return ''
    }

    // Check if translation exists for the current locale
    const translation = obj.translations?.[language]?.[fieldName]

    // Return translated value if available, otherwise fall back to default field
    if (translation !== undefined && translation !== null && translation !== '') {
      return String(translation)
    }

    // Fall back to the default field value
    const defaultValue = obj[fieldName]
    return defaultValue !== undefined && defaultValue !== null ? String(defaultValue) : ''
  }

  return getTranslatedField
}
